'use client';

import { Box, Button, Flex, Skeleton, Stack, Text } from '@instamenu/ui-system-next';
import { Checkout, useCheckoutContext } from '@instamenu/context-checkout';
import { observer } from 'mobx-react-lite';
import { Fulfillment } from '@instamenu/data-access';
import { formatCurrency, formatDistance, roundUpTo } from '@instamenu/formatters';
import { useDistance } from '@instamenu/react-hooks';
import { palette, themeVars } from '@instamenu/ui-vanilla-extract';
import { message } from './estimated-wait.css';
import { useUIStateContext } from '../ui-state-context/ui-state-context';
import { CircleX } from 'lucide-react';

const formatTime = (minutes: number | undefined | null) =>
  typeof minutes !== 'number' ? '- min' : `${roundUpTo(minutes, 5)} min`;

type EstimatedDataProps = {
  isDelivery: boolean;
  checkout: Checkout;
};

const SkeletonView = () => (
  <Flex>
    <Stack css={{ gap: 4, flex: 1, alignItems: 'center' }}>
      <Skeleton width={80} height={15} variant="text" />
      <Skeleton width={120} height={14} variant="text" />
    </Stack>
    <Box css={{ width: 1, height: 24, backgroundColor: 'rgba(0, 0, 0, 0.1)' }} />
    <Stack css={{ gap: 4, flex: 1, alignItems: 'center' }}>
      <Skeleton width={80} height={15} variant="text" />
      <Skeleton width={120} height={14} variant="text" />
    </Stack>
  </Flex>
);

const MessageDisplay = ({ message, isOrder }: { message: string; isOrder?: boolean }) => (
  <Flex
    css={{ padding: !isOrder ? '16px 0' : '', justifyContent: isOrder ? 'flex-start' : 'center' }}
  >
    <Text variant="caption1" css={{ textAlign: 'center' }}>
      {message}
    </Text>
  </Flex>
);

const View = observer(({ isDelivery, checkout }: EstimatedDataProps) => {
  const { distance: distance } = useDistance(checkout?.restaurant.address?.location);

  return (
    <Flex>
      <Stack css={{ gap: 4, flex: 1, alignItems: 'center' }}>
        {!checkout.isAcceptingInstantOrders ? (
          checkout.isAcceptingScheduledOrders ? (
            <Text variant="caption2" css={{ textAlign: 'center' }}>
              Only accepting scheduled orders
            </Text>
          ) : (
            <Stack css={{ textAlign: 'center', gap: 4, alignItems: 'center' }}>
              <CircleX width={24} color={palette.error} />
              <Text variant="caption2">Currently not accepting online orders</Text>
            </Stack>
          )
        ) : (
          <>
            <Text variant="caption2">{isDelivery ? 'Arrives in' : 'Ready in'}</Text>
            <Text variant="caption1" css={{ color: themeVars.color.brand }}>
              {formatTime(
                isDelivery ? checkout.deliveryEstimateMinutes : checkout.restaurant.waitTime,
              )}
            </Text>
          </>
        )}
      </Stack>

      <Box css={{ width: 1, height: 24, backgroundColor: 'rgba(0, 0, 0, 0.1)', flexShrink: 0 }} />

      <Stack css={{ gap: 4, flex: 1, alignItems: 'center' }}>
        <Text variant="caption2">{isDelivery ? 'Delivery fee' : 'Distance'}</Text>
        <Text variant="caption1" css={{ color: themeVars.color.brand }}>
          {isDelivery
            ? checkout.order.deliveryFee
              ? formatCurrency(checkout.order.deliveryFee)
              : '-'
            : distance
              ? formatDistance(distance)
              : '-'}
        </Text>
      </Stack>
    </Flex>
  );
});

export const EstimatedWait = observer(() => {
  const { setIsAddressSelectOpen } = useUIStateContext();
  const checkout = useCheckoutContext();
  const isDelivery = checkout?.mode === Fulfillment.Delivery;

  const showNoAddress = isDelivery && checkout && !checkout.order.deliveryAddress;
  const showOutOfRange = isDelivery && !showNoAddress && !checkout.deliveryQuote?.available;

  if (
    !checkout ||
    (isDelivery &&
      checkout.order.deliveryAddress &&
      (checkout.deliveryQuoteFetching || !checkout.deliveryQuoteIsResolved))
  ) {
    return <SkeletonView />;
  }

  return (
    <div style={{ position: 'relative' }}>
      <Button
        onClick={() => setIsAddressSelectOpen(true)}
        className={message}
        variant="text"
        css={{ opacity: showNoAddress ? 1 : 0, zIndex: showNoAddress ? 1 : 0 }}
      >
        <MessageDisplay message="Enter your address to see delivery details" />
      </Button>
      <div className={message} style={{ opacity: showOutOfRange ? 1 : 0 }}>
        <MessageDisplay message="This address is outside our delivery range" />
      </div>
      <div
        style={{ opacity: !showNoAddress && !showOutOfRange ? 1 : 0, transition: 'opacity 0.1s' }}
      >
        <View isDelivery={isDelivery} checkout={checkout} />
      </div>
    </div>
  );
});
